/*
 * Office.js live-suggestion application, ported from taskpane.js
 * (applyLiveSuggestionToCurrentParagraph + its text-locating helpers).
 * Returns the updated paragraph text; throws the stale/unsupported message so
 * the caller can re-poll. Translated messages are passed in.
 */

import type { LiveSuggestion } from "../state/types";

function locateLiveSuggestion(text: string, before: string, position?: number): number {
  if (!before) return -1;
  const preferred = Number.isFinite(position) ? Math.max(0, position as number) : -1;
  if (preferred >= 0 && text.slice(preferred, preferred + before.length) === before) {
    return preferred;
  }

  const windowStart = Math.max(0, preferred - 30);
  const windowEnd = Math.min(text.length, preferred + before.length + 30);
  const nearby = text.slice(windowStart, windowEnd).indexOf(before);
  if (nearby >= 0) {
    return windowStart + nearby;
  }

  return text.indexOf(before);
}

function countOccurrences(text: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let index = text.indexOf(needle);
  while (index !== -1) {
    count++;
    index = text.indexOf(needle, index + needle.length);
  }
  return count;
}

export async function applyLiveSuggestionToCurrentParagraph(
  change: LiveSuggestion,
  staleMessage: string,
  unsupportedMessage: string
): Promise<string> {
  return Word.run(async (ctx) => {
    const selection = ctx.document.getSelection();
    // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
    const paragraphs = selection.paragraphs;
    paragraphs.load("items");
    await ctx.sync();

    if (!paragraphs.items.length) {
      throw new Error(unsupportedMessage);
    }

    const paragraph = paragraphs.items[0];
    paragraph.load("text");
    await ctx.sync();

    const currentText = paragraph.text || "";
    const before = String(change.before || "");
    const after = String(change.after || "");
    const targetStart = locateLiveSuggestion(currentText, before, change.position);
    if (targetStart < 0) {
      throw new Error(staleMessage);
    }

    const occurrenceIndex = countOccurrences(currentText.slice(0, targetStart), before);
    const matches = paragraph.search(before, { matchCase: true });
    matches.load("items");
    await ctx.sync();

    if (matches.items.length <= occurrenceIndex) {
      throw new Error(staleMessage);
    }

    matches.items[occurrenceIndex].insertText(after, Word.InsertLocation.replace);
    await ctx.sync();

    return (
      currentText.slice(0, targetStart) + after + currentText.slice(targetStart + before.length)
    );
  });
}
